import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconCreditCard,
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconTrash,
  IconAlertTriangle,
  IconPlus,
  IconStar,
  IconStarFilled,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import CustomerBillingProfileServices from "../../services/CustomerBillingProfileServices";
import CustomerProfileServices from "../../services/CustomerProfileServices";
import UserContext from "../../contexts/UserContext";
import CustomerProfileModal from "./modals/CustomerProfileModal";

const CustomerBillingProfilesList = () => {
  const { 
    isOrganization, 
    user, 
  } = useContext(UserContext);
  
  const [loading, setLoading] = useState(false);
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [billingTypeFilter, setBillingTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Estados para modal
  const [showModal, setShowModal] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [modalMode, setModalMode] = useState("create"); // create, edit, view

  useEffect(() => {
    // Verificar se o usuário tem permissão para ver perfis de faturamento
    if (isOrganization() && user?.superuser_level <= 2) {
      loadBillingProfiles();
    }
  }, [currentPage, searchTerm, billingTypeFilter, statusFilter]);

  const loadBillingProfiles = async () => {
    try {
      setLoading(true);
      
      const filters = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm,
        billing_type: billingTypeFilter !== "all" ? billingTypeFilter : undefined,
        is_active: statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined,
      };

      const params = CustomerBillingProfileServices.buildSearchParams(filters);
      const billingProfilesData = await CustomerBillingProfileServices.listCustomerBillingProfiles(params);
      
      // Formatar dados para exibição
      const formattedProfiles = billingProfilesData.data.results?.map(profile => 
        CustomerBillingProfileServices.formatBillingProfileData(profile)
      ) || [];

      setBillingProfiles(formattedProfiles);
      setTotalPages(billingProfilesData.data.total_pages || 1);
      setTotalCount(billingProfilesData.data.count || 0);
    } catch (error) {
      console.error("Erro ao carregar perfis de faturamento:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os perfis de faturamento.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleBillingTypeFilter = (type) => {
    setBillingTypeFilter(type);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // Verificar permissões para diferentes ações
  const canViewProfile = (profile) => {
    return CustomerBillingProfileServices.canPerformAction("view", { isOrganization, user }, profile);
  };

  const canEditProfile = (profile) => {
    return CustomerBillingProfileServices.canPerformAction("edit", { isOrganization, user }, profile);
  };

  const canDeleteProfile = (profile) => {
    return CustomerBillingProfileServices.canPerformAction("delete", { isOrganization, user }, profile);
  };

  const canSetDefault = (profile) => {
    return CustomerBillingProfileServices.canPerformAction("set_default", { isOrganization, user }, profile);
  };

  // Handlers para ações
  const handleCreateProfile = () => {
    setSelectedProfileId(null);
    setModalMode("create");
    setShowModal(true);
  };

  const handleViewProfile = (profileId) => {
    setSelectedProfileId(profileId);
    setModalMode("view");
    setShowModal(true);
  };

  const handleEditProfile = (profileId) => {
    if (canEditProfile()) {
      setSelectedProfileId(profileId);
      setModalMode("edit");
      setShowModal(true);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para editar perfis de faturamento.",
      });
    }
  };

  const handleDeleteProfile = async (profile) => {
    if (!canDeleteProfile(profile)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para deletar perfis de faturamento.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Confirmar Exclusão",
      text: `Deseja excluir o perfil "${profile.profile_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      await CustomerBillingProfileServices.deleteCustomerBillingProfile(profile.id);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Perfil de faturamento excluído com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadBillingProfiles();
    } catch (error) {
      console.error("Erro ao excluir perfil:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível excluir o perfil de faturamento.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleSetDefaultProfile = async (profile) => {
    if (!canSetDefault(profile)) {
      Swal.fire({
        icon: "warning",
        title: "Sem Permissão",
        text: "Você não tem permissão para definir perfis padrão.",
      });
      return;
    }

    if (profile.is_default) {
      Swal.fire({
        icon: "info",
        title: "Perfil Já é Padrão",
        text: "Este perfil já é o padrão para o cliente.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Definir como Padrão",
      text: `Deseja definir "${profile.profile_name}" como perfil padrão?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, definir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#007bff",
    });

    if (!result.isConfirmed) return;

    try {
      await CustomerBillingProfileServices.setDefaultBillingProfile(profile.id);
      
      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Perfil definido como padrão com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadBillingProfiles();
    } catch (error) {
      console.error("Erro ao definir perfil como padrão:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível definir o perfil como padrão.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleModalSuccess = () => {
    loadBillingProfiles(); // Recarregar lista após alterações
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Anterior
        </Button>
        {pages}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </Button>
      </div>
    );
  };

  return (
    <div className="customer-billing-profiles">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <h3 className="d-flex">
              <IconCreditCard size={30} className="me-2" />
              Perfis de Faturamento
            </h3>
            <p className="text-muted">
              Gerencie os perfis de faturamento dos seus clientes
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleCreateProfile}>
              <IconPlus size={16} className="me-1" />
              Novo Perfil
            </Button>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <IconSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por perfil ou cliente..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <IconFilter size={16} />
                </InputGroup.Text>
                <Form.Select
                  value={billingTypeFilter}
                  onChange={(e) => handleBillingTypeFilter(e.target.value)}
                >
                  <option value="all">Todos os Tipos</option>
                  <option value="CREDIT_CARD">Cartão de Crédito</option>
               
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={2}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
              >
                <option value="all">Todos os Status</option>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <div className="text-muted small">
                Total: {totalCount} perfis
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de Perfis de Faturamento */}
      <Card>
        <Card.Header className="d-flex gap-2 align-items-center">
          <IconCreditCard size={20} />
          <h5 className="mb-0">Lista de Perfis de Faturamento</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Carregando perfis...</p>
              </div>
            </div>
          ) : billingProfiles.length > 0 ? (
            <>
              <Table responsive hover className="billing-profiles-table">
                <thead>
                  <tr>
                    <th>Perfil</th>
                    <th>Cliente</th>
                    <th>Tipo</th>
                    <th>Informações</th>
                    <th>Status</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {billingProfiles.map((profile) => (
                    <tr key={profile.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div>
                            <div className="fw-bold">
                              {profile.profile_name}
                              {profile.is_default && (
                                <IconStarFilled size={14} className="ms-1 text-warning" />
                              )}
                            </div>
                            {profile.is_default && (
                              <small className="text-warning">Perfil Padrão</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-bold">{profile.customer_profile_name}</div>
                          <small className="text-muted">{profile.customer_profile_email}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg={profile.billingTypeVariant}>
                          {profile.billingTypeText}
                        </Badge>
                      </td>
                      <td>
                        <div className="small">
                          {profile.formattedCardInfo && (
                            <div>{profile.formattedCardInfo}</div>
                          )}
                          {profile.card_holder_name && (
                            <div className="text-muted">{profile.card_holder_name}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg={profile.statusVariant}>
                          {profile.statusText}
                        </Badge>
                      </td>
                      <td>
                        <span className="date-info">
                          {profile.formattedCreatedAt}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <OverlayTrigger
                            overlay={<Tooltip>Ver detalhes</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewProfile(profile.id)}
                              disabled={!canViewProfile(profile)}
                            >
                              <IconEye size={14} />
                            </Button>
                          </OverlayTrigger>
                          
                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {canEditProfile(profile) 
                                  ? "Editar perfil" 
                                  : "Sem permissão para editar"}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleEditProfile(profile.id)}
                              disabled={!canEditProfile(profile)}
                            >
                              <IconEdit size={14} />
                            </Button>
                          </OverlayTrigger>

                          {!profile.is_default && (
                            <OverlayTrigger
                              overlay={<Tooltip>Definir como padrão</Tooltip>}
                            >
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleSetDefaultProfile(profile)}
                                disabled={!canSetDefault(profile)}
                              >
                                <IconStar size={14} />
                              </Button>
                            </OverlayTrigger>
                          )}

                          <OverlayTrigger
                            overlay={
                              <Tooltip>
                                {canDeleteProfile(profile) 
                                  ? "Excluir perfil" 
                                  : "Sem permissão para excluir"}
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteProfile(profile)}
                              disabled={!canDeleteProfile(profile)}
                            >
                              <IconTrash size={14} />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          ) : !isOrganization() ? (
            <Alert variant="warning" className="text-center">
              <IconAlertTriangle size={24} className="mb-2" />
              <p className="mb-0">
                Esta funcionalidade está disponível apenas para organizações.
              </p>
            </Alert>
          ) : user?.superuser_level > 2 ? (
            <Alert variant="warning" className="text-center">
              <IconAlertTriangle size={24} className="mb-2" />
              <p className="mb-0">
                Você não tem permissão para visualizar perfis de faturamento.
              </p>
            </Alert>
          ) : (
            <Alert variant="info" className="text-center">
              <IconCreditCard size={24} className="mb-2" />
              <p className="mb-0">
                {searchTerm || billingTypeFilter !== "all" || statusFilter !== "all"
                  ? "Nenhum perfil encontrado com os filtros aplicados."
                  : "Nenhum perfil de faturamento encontrado."}
              </p>
              <small>
                {searchTerm || billingTypeFilter !== "all" || statusFilter !== "all"
                  ? "Tente ajustar os filtros de busca."
                  : "Crie seu primeiro perfil de faturamento para começar."}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Modal de criação/edição */}
      <CustomerProfileModal
        show={showModal}
        onHide={() => setShowModal(false)}
        profileId={selectedProfileId}
        mode={modalMode}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default CustomerBillingProfilesList;